import { memo, useMemo } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { AUTOCOMPLETE_SX } from './filterStyles';
import { toArray } from './filterUtils';

interface RequirementMultiSelectProps {
    /** Available requirement identifiers (should be pre-sorted if needed) */
    options: string[];
    /** Currently selected requirement identifiers */
    value: string[] | undefined;
    /** Called when selection changes */
    onChange: (identifiers: string[] | undefined) => void;
    /** Optional label override */
    label?: string;
    /** Optional minimum width override */
    minWidth?: number;
    /** Optional maximum width override */
    maxWidth?: number;
}

function RequirementMultiSelectComponent({
    options,
    value,
    onChange,
    label = 'Requirement',
    minWidth = 220,
    maxWidth = 350,
}: RequirementMultiSelectProps) {
    // Get selected values that exist in options
    const selectedValues = useMemo(() => {
        const identifiers = toArray(value);
        return identifiers.filter(id => options.includes(id));
    }, [options, value]);

    return (
        <Autocomplete
            multiple
            size="small"
            sx={{ minWidth, maxWidth, ...AUTOCOMPLETE_SX }}
            options={options}
            value={selectedValues}
            onChange={(_, values) => onChange(values.length > 0 ? values : undefined)}
            renderInput={params => <TextField {...params} label={label} />}
            renderValue={(values, getItemProps) =>
                values.map((val, index) => {
                    const { key, ...tagProps } = getItemProps({ index });
                    return <Chip key={key} label={val} size="small" {...tagProps} />;
                })
            }
            slotProps={{
                popper: {
                    style: { width: 'fit-content' },
                },
            }}
        />
    );
}

export const RequirementMultiSelect = memo(RequirementMultiSelectComponent);
